import { useContext, useEffect, useRef } from 'react'

import { useGgoResult, useGgoState } from '@lib/services/api/cheetah-provider'
import { useBrowserService, useExperimentService, useUserService } from '@lib/hooks'
import { AI_HUMANIZER_SLUG, PARAPHRASING_TOOL_SLUG } from '@lib/constants'
import { useCheetahResultStateTracking } from '@components/section/go-templates-tool/go-templates-context'
import { PageContext } from '@contexts/page-context'
import { DEFAULT_NEXT_BEST_ACTION_COPY, DEFAULT_TOOL_API_ERROR_MESSAGE, minimumLoadingTime } from './constants'
import { GoTemplatesOutput } from './output/go-templates'
import { isGoTemplatesToolType, ToolType } from './types'

const getGoTemplatesNextBestActionCopy = (slug: string) => {
  switch (slug) {
    case PARAPHRASING_TOOL_SLUG:
      return 'Keep improving this version with clarity, tone, and style suggestions.'
    case AI_HUMANIZER_SLUG:
      return 'Keep your voice across everything you write with real-time suggestions.'
    default:
      return DEFAULT_NEXT_BEST_ACTION_COPY
  }
}

export const useCheetahToolState = (
  toolType: ToolType,
  setIsLoading: (isLoading: boolean) => void,
  setOutputContent: (outputContent: React.ReactNode) => void,
  setOutputTextToCopy: (outputTextToCopy: string | null) => void,
  setNextBestActionCopy: (nextBestActionCopy: string | null) => void,
  setToolError: (toolError: string) => void,
  setShowToolsGoogleOneTap: (show: boolean) => void,
  isEligibleForToolsGoogleOneTap: boolean
) => {
  const { cheetahResultState, error: cheetahError } = useGgoState()
  const output = useGgoResult()
  const { getToolsGoogleOneTapAssignment } = useExperimentService()
  const { isMobileOrTablet } = useBrowserService()

  const { isAnonymous, isPremium } = useUserService().currentUser
  const { pageId, slug } = useContext(PageContext)

  const cheetahLoadingTimestamp = useRef(-1)

  // Make sure we are loading for a minimal amount of time when done. cheetahLoadingTimestamp is updated in the useCheetahResultStateTracking hook.
  useEffect(() => {
    if (isGoTemplatesToolType(toolType)) {
      if (cheetahResultState === 'loading' || cheetahResultState === 'streaming') {
        setIsLoading(true)
      } else if (cheetahResultState === 'done') {
        if (cheetahLoadingTimestamp.current > -1) {
          const timeElapsed = Date.now() - cheetahLoadingTimestamp.current
          if (timeElapsed >= minimumLoadingTime) {
            setIsLoading(false)
          } else {
            setTimeout(() => setIsLoading(false), minimumLoadingTime - timeElapsed)
          }
        } else {
          setIsLoading(false)
        }
      } else {
        setIsLoading(false)
      }
    }
  }, [cheetahResultState, toolType, setIsLoading])

  useEffect(() => {
    if (output && cheetahResultState === 'done') {
      setOutputContent(<GoTemplatesOutput output={output} />)
      setOutputTextToCopy(output)
      setNextBestActionCopy(getGoTemplatesNextBestActionCopy(slug))
      if (isEligibleForToolsGoogleOneTap && getToolsGoogleOneTapAssignment(isMobileOrTablet) === 'task_complete') {
        setShowToolsGoogleOneTap(true)
      }
    }
  }, [
    cheetahResultState,
    setOutputContent,
    setOutputTextToCopy,
    output,
    setNextBestActionCopy,
    slug,
    setShowToolsGoogleOneTap,
    isEligibleForToolsGoogleOneTap,
    getToolsGoogleOneTapAssignment,
    isMobileOrTablet,
  ])

  useEffect(() => {
    if (cheetahError) {
      // Pro users cannot easily get more prompts, so we show a generic error message for now.
      // @todo improve messaging for Pro users (https://grammarly.atlassian.net/browse/G2A-1968).
      let errorMessage = ''
      switch (cheetahError.kind) {
        case 'promptsLimitExceeded': {
          if (!isPremium) {
            errorMessage = `${isAnonymous ? 'Sign up' : 'Upgrade'} for additional access.`
          }
          break
        }
        case 'unsupportedLanguage': {
          errorMessage = 'Assistance is currently only available in English.'
          break
        }
        default: {
          errorMessage = DEFAULT_TOOL_API_ERROR_MESSAGE
        }
      }

      setToolError(errorMessage)
    }
  }, [cheetahError, setToolError, isAnonymous, isPremium])

  useCheetahResultStateTracking(cheetahResultState, cheetahLoadingTimestamp, output, pageId, slug, cheetahError)
}
